import { createAsyncThunk, createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { agentAxiosObj, axiosObj } from "../../config/apiClients";
import { sendOverChatSocket } from "../../services/chatSocket";
import { getActorTypeFromHost } from "../../utils/helpers/getActorType";
import { playMessageTone } from "../../utils/helpers/playMessageTone";
import {
  ChatContact,
  ChatConversation,
  ChatInbox,
  ChatMessage,
  ChatUnreadCounts,
  ChatUploadUrl,
} from "../../utils/types";
import { ChatState } from "../types";
import type { AppDispatch, RootState } from "../store";

const chatApi = getActorTypeFromHost() === "salesAgent" ? agentAxiosObj : axiosObj;

const initialState: ChatState = {
  contacts: [],
  conversations: [],
  hasMoreConversations: false,
  isLoading: false,
  isLoadingMore: false,
  error: null,
  inbox: "staff",
  unread: { staff: 0, support: 0 },
  activeId: null,
  messages: [],
  hasMore: false,
  isMessagesLoading: false,
  messagesError: null,
  isSending: false,
  typingPeerId: null,
  uploadProgress: null,
};

export type ChatMessageEvent = { conversationId: string; message: ChatMessage };
export type ChatReadEvent = { conversationId: string; readerId: string; readAt: string };
export type ChatDeletedEvent = { conversationId: string; messageId: string };
export type ChatPresenceEvent = { accountId: string; isOnline: boolean; lastSeenAt?: string };
export type ChatPinnedEvent = { conversationId: string; pinnedMessage: ChatMessage | null };

const chatThunk = <Result, Arg = void>(
  type: string,
  request: (arg: Arg, dispatch: Dispatch) => Promise<{ data: { payload: Result } }>,
  fallbackMessage: string,
) =>
  createAsyncThunk<Result, Arg, { rejectValue: string }>(type, async (arg, { dispatch, rejectWithValue }) => {
    try {
      return (await request(arg, dispatch)).data.payload;
    } catch (error) {
      return rejectWithValue((error as { message?: string })?.message || fallbackMessage);
    }
  });

export const getChatContacts = chatThunk<{ contacts: ChatContact[] }>(
  "chat/getContacts",
  () => chatApi.get("/chat/contacts"),
  "Failed to load contacts",
);

// The first page of the inbox, or with `before` the page of older conversations after the loaded ones
export const getChatConversations = chatThunk<
  { conversations: ChatConversation[]; hasMore: boolean },
  { before?: string; search?: string } | void
>(
  "chat/getConversations",
  (query) => chatApi.get("/chat/conversations", { params: query ?? {} }),
  "Failed to load conversations",
);

export const getChatUnreadCounts = chatThunk<{ unread: ChatUnreadCounts }>(
  "chat/getUnreadCounts",
  () => chatApi.get("/chat/conversations/unread"),
  "Failed to load unread counts",
);

export const openChatConversation = chatThunk<{ conversation: ChatConversation }, ChatContact>(
  "chat/openConversation",
  (contact) => chatApi.post("/chat/conversations", { participantId: contact.id, participantKind: contact.kind }),
  "Failed to open conversation",
);

export const getChatMessages = chatThunk<
  { messages: ChatMessage[]; hasMore: boolean },
  { conversationId: string; before?: string }
>(
  "chat/getMessages",
  ({ conversationId, before }) =>
    chatApi.get(`/chat/conversations/${conversationId}/messages`, { params: { before } }),
  "Failed to load messages",
);

// Asks the API for one upload URL per file, then PUTs every file straight to S3 and reports their combined progress.
// The PUT uses plain axios on purpose: S3 must not get the API's session cookies or its 401 refresh-and-retry.
const uploadChatFiles = async (conversationId: string, files: File[], dispatch: Dispatch) => {
  const { data } = await chatApi.post<{ payload: { uploads: ChatUploadUrl[] } }>(
    `/chat/conversations/${conversationId}/attachments/upload-urls`,
    { files: files.map(({ name, size }) => ({ name, size })) },
  );
  const { uploads } = data.payload;

  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const uploadedBytes = files.map(() => 0);
  try {
    await Promise.all(files.map((file, index) =>
      axios.put(uploads[index].url, file, {
        headers: uploads[index].headers,
        onUploadProgress: ({ loaded }) => {
          uploadedBytes[index] = loaded;
          const uploadedTotal = uploadedBytes.reduce((sum, bytes) => sum + bytes, 0);
          dispatch(chatUploadProgressed(Math.round((uploadedTotal / totalBytes) * 100)));
        },
      }),
    ));
  } catch {
    throw new Error("Could not upload the files. Please try again.");
  }

  return uploads.map(({ key }, index) => ({ key, name: files[index].name }));
};

export const sendChatMessage = chatThunk<
  { message?: ChatMessage },
  { conversationId: string; body: string; files?: File[]; replyToId?: string }
>(
  "chat/sendMessage",
  async ({ conversationId, body, files = [], replyToId }, dispatch) => {
    // Text travels over the chat socket and comes back as a chat:message event; files go to S3 first.
    if (!files.length) {
      await sendOverChatSocket({ conversationId, body, replyToId });
      return { data: { payload: {} } };
    }

    const attachments = await uploadChatFiles(conversationId, files, dispatch);
    return chatApi.post(`/chat/conversations/${conversationId}/messages`, { body, replyToId, attachments });
  },
  "Failed to send message",
);

export const forwardChatMessage = chatThunk<
  { conversationId: string },
  { conversationId: string; messageId: string; target: ChatContact }
>(
  "chat/forwardMessage",
  ({ conversationId, messageId, target }) =>
    chatApi.post(`/chat/conversations/${conversationId}/messages/${messageId}/forward`, {
      participantId: target.id,
      participantKind: target.kind,
    }),
  "Failed to forward message",
);

export const fetchChatAttachment = async (conversationId: string, messageId: string, index: number): Promise<Blob> => {
  const response = await chatApi.get(
    `/chat/conversations/${conversationId}/messages/${messageId}/attachments/${index}`,
    { responseType: "blob" },
  );
  return response.data;
};

export const pinChatMessage = chatThunk<
  { pinnedMessage: ChatMessage | null },
  { conversationId: string; messageId: string | null }
>(
  "chat/pinMessage",
  ({ conversationId, messageId }) => chatApi.put(`/chat/conversations/${conversationId}/pin`, { messageId }),
  "Failed to update pinned message",
);

export const deleteChatMessage = chatThunk<unknown, ChatDeletedEvent>(
  "chat/deleteMessage",
  ({ conversationId, messageId }) => chatApi.delete(`/chat/conversations/${conversationId}/messages/${messageId}`),
  "Failed to delete message",
);

export const markChatRead = chatThunk<unknown, string>(
  "chat/markRead",
  (conversationId) => chatApi.post(`/chat/conversations/${conversationId}/read`),
  "Failed to mark conversation as read",
);

const addInboxUnread = (state: ChatState, inbox: ChatInbox, amount: number) => {
  if (inbox === "support") state.unread.support += amount;
  else state.unread.staff += amount;
};

const clearConversationUnread = (state: ChatState, conversation: ChatConversation) => {
  addInboxUnread(state, conversation.type, -conversation.unread);
  conversation.unread = 0;
};

const applyMessage = (state: ChatState, { conversationId, message }: ChatMessageEvent) => {
  const index = state.conversations.findIndex((conversation) => conversation.id === conversationId);
  if (index === -1) return;

  const [conversation] = state.conversations.splice(index, 1);
  conversation.lastMessage = message;
  if (message.senderId === conversation.peer.id) {
    conversation.unread += 1;
    addInboxUnread(state, conversation.type, 1);
  }
  if (message.senderId === state.typingPeerId) state.typingPeerId = null;
  state.conversations.unshift(conversation);

  if (conversationId === state.activeId && !state.messages.some(({ _id }) => _id === message._id)) {
    state.messages.push(message);
  }
};

const applyPinned = (state: ChatState, { conversationId, pinnedMessage }: ChatPinnedEvent) => {
  const conversation = state.conversations.find(({ id }) => id === conversationId);
  if (conversation) conversation.pinnedMessage = pinnedMessage;
};

const applyDeleted = (state: ChatState, { conversationId, messageId }: ChatDeletedEvent) => {
  const conversation = state.conversations.find(({ id }) => id === conversationId);
  const deleted = [conversation?.lastMessage, ...(conversationId === state.activeId ? state.messages : [])];
  if (conversation?.pinnedMessage?._id === messageId) conversation.pinnedMessage = null;

  deleted.forEach((message) => {
    if (message?._id !== messageId) return;
    message.body = "";
    message.attachments = undefined;
    message.deletedAt = new Date().toISOString();
  });
};

const applyRead = (state: ChatState, { conversationId, readerId, readAt }: ChatReadEvent) => {
  const conversation = state.conversations.find(({ id }) => id === conversationId);
  if (!conversation) return;
  if (readerId === conversation.peer.id) conversation.peerReadAt = readAt;
  else clearConversationUnread(state, conversation);
};

const openConversation = (state: ChatState, conversationId: string | null) => {
  if (conversationId === state.activeId) return;
  // Show the inbox tab the opened conversation belongs to, e.g. after "Chat" on the Clients page
  const conversation = state.conversations.find(({ id }) => id === conversationId);
  if (conversation) state.inbox = conversation.type;
  state.activeId = conversationId;
  state.messages = [];
  state.hasMore = false;
  state.messagesError = null;
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setActiveConversation: (state, action: PayloadAction<string | null>) => {
      openConversation(state, action.payload);
    },
    setChatInbox: (state, action: PayloadAction<ChatInbox>) => {
      state.inbox = action.payload;
    },
    chatMessageReceived: (state, action: PayloadAction<ChatMessageEvent>) => {
      applyMessage(state, action.payload);
    },
    chatReadReceived: (state, action: PayloadAction<ChatReadEvent>) => {
      applyRead(state, action.payload);
    },
    chatMessageDeleted: (state, action: PayloadAction<ChatDeletedEvent>) => {
      applyDeleted(state, action.payload);
    },
    chatTypingChanged: (state, action: PayloadAction<string | null>) => {
      state.typingPeerId = action.payload;
    },
    chatUploadProgressed: (state, action: PayloadAction<number>) => {
      state.uploadProgress = action.payload;
    },
    chatPinnedReceived: (state, action: PayloadAction<ChatPinnedEvent>) => {
      applyPinned(state, action.payload);
    },
    chatPresenceReceived: (state, action: PayloadAction<ChatPresenceEvent>) => {
      const { accountId, isOnline, lastSeenAt } = action.payload;
      const contacts = [...state.contacts, ...state.conversations.map(({ peer }) => peer)];

      contacts.forEach((contact) => {
        if (contact.id !== accountId) return;
        contact.isOnline = isOnline;
        contact.lastSeenAt = lastSeenAt;
      });
    },
    clearChat: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(getChatContacts.fulfilled, (state, action) => {
        state.contacts = action.payload.contacts;
      })
      .addCase(getChatConversations.pending, (state, action) => {
        if (action.meta.arg?.before) state.isLoadingMore = true;
        else state.isLoading = true;
        state.error = null;
      })
      .addCase(getChatConversations.fulfilled, (state, action) => {
        const { conversations, hasMore } = action.payload;
        state.isLoading = false;
        state.isLoadingMore = false;
        state.hasMoreConversations = hasMore;

        if (action.meta.arg?.before) {
          const loadedIds = new Set(state.conversations.map(({ id }) => id));
          state.conversations.push(...conversations.filter(({ id }) => !loadedIds.has(id)));
          return;
        }

        const active = state.conversations.find(({ id }) => id === state.activeId);
        state.conversations = conversations;
        // A chat opened but not yet written in is not returned by the server.
        if (active && !state.conversations.some(({ id }) => id === active.id)) {
          state.conversations.unshift(active);
        }
      })
      .addCase(getChatConversations.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error = action.payload ?? null;
      })
      .addCase(getChatUnreadCounts.fulfilled, (state, action) => {
        state.unread = action.payload.unread;
      })
      .addCase(openChatConversation.fulfilled, (state, action) => {
        const { conversation } = action.payload;
        if (!state.conversations.some(({ id }) => id === conversation.id)) {
          state.conversations.unshift(conversation);
        }
        openConversation(state, conversation.id);
      })
      .addCase(getChatMessages.pending, (state) => {
        state.isMessagesLoading = true;
        state.messagesError = null;
      })
      .addCase(getChatMessages.fulfilled, (state, action) => {
        const { conversationId, before } = action.meta.arg;
        if (conversationId !== state.activeId) return;
        state.isMessagesLoading = false;
        state.hasMore = action.payload.hasMore;
        state.messages = before ? [...action.payload.messages, ...state.messages] : action.payload.messages;
      })
      .addCase(getChatMessages.rejected, (state, action) => {
        if (action.meta.arg.conversationId !== state.activeId) return;
        state.isMessagesLoading = false;
        state.messagesError = action.payload ?? null;
      })
      .addCase(sendChatMessage.pending, (state, action) => {
        state.isSending = true;
        state.uploadProgress = action.meta.arg.files?.length ? 0 : null;
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        state.isSending = false;
        state.uploadProgress = null;
        const { message } = action.payload;
        if (message) applyMessage(state, { conversationId: action.meta.arg.conversationId, message });
      })
      .addCase(sendChatMessage.rejected, (state) => {
        state.isSending = false;
        state.uploadProgress = null;
      })
      .addCase(pinChatMessage.fulfilled, (state, action) => {
        applyPinned(state, { conversationId: action.meta.arg.conversationId, pinnedMessage: action.payload.pinnedMessage });
      })
      .addCase(deleteChatMessage.fulfilled, (state, action) => {
        applyDeleted(state, action.meta.arg);
      })
      .addCase(markChatRead.pending, (state, action) => {
        const conversation = state.conversations.find(({ id }) => id === action.meta.arg);
        if (conversation) clearConversationUnread(state, conversation);
      });
  },
});

export const {
  setActiveConversation,
  setChatInbox,
  chatMessageReceived,
  chatReadReceived,
  chatMessageDeleted,
  chatTypingChanged,
  chatUploadProgressed,
  chatPinnedReceived,
  chatPresenceReceived,
  clearChat,
} = chatSlice.actions;

export const describeChatMessage = ({
  body,
  attachments = [],
  deletedAt,
}: Pick<ChatMessage, "body" | "attachments" | "deletedAt">) => {
  if (deletedAt) return "Message deleted";
  if (body) return body;
  if (attachments.length > 1) return `${attachments.length} files`;
  if (!attachments.length) return "";
  return attachments[0].mimeType.startsWith("audio/") ? "Voice message" : attachments[0].name;
};

export const receiveChatMessage =
  (event: ChatMessageEvent) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const findConversation = () => getState().chat.conversations.find(({ id }) => id === event.conversationId);

    if (findConversation()) {
      dispatch(chatMessageReceived(event));
    } else {
      // A chat that is not loaded yet (new, or on an older page): its message puts it on the first page
      await dispatch(getChatConversations());
      dispatch(getChatUnreadCounts());
    }

    const conversation = findConversation();
    if (!conversation || event.message.senderId !== conversation.peer.id) return;

    const preview = describeChatMessage(event.message);
    playMessageTone();
    if (document.hidden && "Notification" in window && Notification.permission === "granted") {
      new Notification(conversation.peer.name, { body: preview, tag: event.conversationId });
    }
    if (getState().chat.activeId === event.conversationId) return;
    return { conversationId: event.conversationId, peer: conversation.peer, preview };
  };

export const resyncChat = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const { activeId } = getState().chat;
  dispatch(getChatConversations());
  dispatch(getChatUnreadCounts());
  if (activeId) dispatch(getChatMessages({ conversationId: activeId }));
};

// From the server's counts, because older conversations may not be loaded
export const selectChatUnread = (state: RootState) => state.chat.unread.staff + state.chat.unread.support;

export const selectActiveConversation = (state: RootState) =>
  state.chat.conversations.find(({ id }) => id === state.chat.activeId);

export default chatSlice.reducer;
